import { apiClient } from './api.client';

export interface TrialDashboardData {
  trialAccount: {
    id: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
    budgetAllocated: number;
    budgetUsed: number;
    daysRemaining: number | null;
    budgetRemaining: number;
    isExpired: boolean;
    isActive: boolean;
  };
  manufacturer: {
    id: string;
    name: string;
    contactEmail: string;
  };
  usageHistory: Array<{
    id: string;
    operationType: string;
    costAmount: number;
    remainingBudget: number;
    operationDetails: any;
    createdAt: string;
  }>;
  conversionOptions: {
    canRequestFullRegistration: boolean;
    fullRegistrationUrl: string;
    upgradePromptMessage: string;
  };
}

export interface TrialUsageStats {
  totalOperations: number;
  operationsByType: Record<string, number>;
  averageCostPerOperation: number;
  mostUsedOperation: string;
  budgetUtilizationPercentage: number;
}

export interface TrialConversionRequest {
  reason?: string;
  expectedVolume?: number;
  businessPlan?: string;
}

class TrialDashboardService {
  /**
   * Get trial dashboard data for the current user
   */
  async getTrialDashboard(): Promise<TrialDashboardData> {
    try {
      const response = await apiClient.get('/trial/dashboard');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to load trial dashboard');
      }
      throw new Error('Failed to load trial dashboard');
    }
  }

  /**
   * Lightweight trial status check for the current user
   */
  async getTrialStatus(): Promise<{ isTrial: boolean; status?: string }> {
    try {
      const response = await apiClient.get('/trial/status');
      const data = response.data?.data ?? response.data;

      return {
        isTrial: true,
        status: data?.status
      };
    } catch (error: any) {
      // 404/400 means not a trial account; surface other errors
      const status = error?.response?.status;
      if (status === 404 || status === 400) {
        return { isTrial: false };
      }

      throw error;
    }
  }

  /**
   * Get trial usage statistics
   */
  async getTrialUsageStats(): Promise<TrialUsageStats> {
    try {
      const response = await apiClient.get('/trial/usage-stats');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to load usage statistics');
      }
      throw new Error('Failed to load usage statistics');
    }
  }

  /**
   * Request full registration conversion
   */
  async requestFullRegistration(data: TrialConversionRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/trial/request-full-registration', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return { success: false, message: error.response.data.message || 'Failed to request full registration' };
      }
      throw new Error('Failed to request full registration');
    }
  }

  /**
   * Format trial status for display
   */
  formatTrialStatus(status: string): string {
    switch (status) {
      case 'PENDING_TRIAL_APPROVAL':
        return 'Pending Approval';
      case 'TRIAL_ACTIVE':
        return 'Active Trial';
      case 'TRIAL_EXPIRED':
        return 'Trial Expired';
      case 'FULL_REGISTRATION_PENDING_APPROVAL':
        return 'Full Registration Pending';
      case 'ACTIVE':
        return 'Active Account';
      case 'BLOCKED':
        return 'Blocked';
      default:
        return status;
    }
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'PENDING_TRIAL_APPROVAL':
        return 'orange';
      case 'TRIAL_ACTIVE':
        return 'green';
      case 'TRIAL_EXPIRED':
        return 'red';
      case 'FULL_REGISTRATION_PENDING_APPROVAL':
        return 'blue';
      case 'ACTIVE':
        return 'green';
      case 'BLOCKED':
        return 'red';
      default:
        return 'gray';
    }
  }

  /**
   * Calculate days remaining
   */
  calculateDaysRemaining(endDate: string | null): number | null {
    if (!endDate) return null;
    
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  /**
   * Calculate budget utilization percentage
   */
  calculateBudgetUtilization(budgetUsed: number, budgetAllocated: number): number {
    if (budgetAllocated === 0) return 0;
    return Math.round((budgetUsed / budgetAllocated) * 100);
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Get upgrade urgency level
   */
  getUpgradeUrgency(daysRemaining: number | null, budgetRemaining: number): 'low' | 'medium' | 'high' | 'critical' {
    if (daysRemaining === null) return 'low';
    
    if (daysRemaining <= 1 || budgetRemaining <= 50) {
      return 'critical';
    } else if (daysRemaining <= 3 || budgetRemaining <= 200) {
      return 'high';
    } else if (daysRemaining <= 7 || budgetRemaining <= 500) {
      return 'medium';
    }
    
    return 'low';
  }

  /**
   * Get upgrade prompt message based on urgency
   */
  getUpgradePromptMessage(urgency: 'low' | 'medium' | 'high' | 'critical', daysRemaining: number | null, budgetRemaining: number): string {
    switch (urgency) {
      case 'critical':
        if (daysRemaining !== null && daysRemaining <= 1) {
          return `Your trial expires ${daysRemaining === 0 ? 'today' : 'tomorrow'}! Upgrade now to continue using the platform.`;
        }
        return `Only ${this.formatCurrency(budgetRemaining)} remaining! Upgrade now to avoid service interruption.`;
      
      case 'high':
        if (daysRemaining !== null && daysRemaining <= 3) {
          return `Your trial expires in ${daysRemaining} days. Upgrade soon to maintain access.`;
        }
        return `Low budget remaining: ${this.formatCurrency(budgetRemaining)}. Consider upgrading soon.`;
      
      case 'medium':
        return `Your trial is progressing well. Consider upgrading to unlock full features.`;
      
      default:
        return `Enjoying your trial? Upgrade anytime to access unlimited features.`;
    }
  }

  /**
   * Check if operation is blocked for trial accounts
   */
  isOperationBlocked(operationType: string): boolean {
    const blockedOperations = [
      'CREDIT_TOPUP',
      'PAYMENT_PROCESSING',
      'BULK_EXPORT',
      'ADVANCED_ANALYTICS',
      'API_ACCESS'
    ];
    
    return blockedOperations.includes(operationType);
  }

  /**
   * Get blocked operation message
   */
  getBlockedOperationMessage(operationType: string): string {
    switch (operationType) {
      case 'CREDIT_TOPUP':
        return 'Credit top-up is not available during trial. Upgrade to add credits to your account.';
      case 'PAYMENT_PROCESSING':
        return 'Payment processing is not available during trial. Upgrade to enable payment features.';
      case 'BULK_EXPORT':
        return 'Bulk data export is not available during trial. Upgrade to access advanced export features.';
      case 'ADVANCED_ANALYTICS':
        return 'Advanced analytics are not available during trial. Upgrade to access detailed insights.';
      case 'API_ACCESS':
        return 'API access is not available during trial. Upgrade to integrate with external systems.';
      default:
        return 'This feature is not available during trial. Upgrade to access all platform features.';
    }
  }
}

export const trialDashboardService = new TrialDashboardService();
